// Reader Service
// Handles reader discovery, reading queue, and content recommendations

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

// Cloud Functions
const getRecommendedNewsletters = httpsCallable(functions, 'getRecommendedNewsletters');
const getReadingQueue = httpsCallable(functions, 'getReadingQueue');
const addToReadingQueue = httpsCallable(functions, 'addToReadingQueue');
const removeFromReadingQueue = httpsCallable(functions, 'removeFromReadingQueue');
const updateReadingProgress = httpsCallable(functions, 'updateReadingProgress');
const getBreakingNews = httpsCallable(functions, 'getBreakingNews');

export interface Newsletter {
  id: string;
  title: string;
  author: string;
  authorId: string;
  description: string;
  category: string;
  subscriberCount: number;
  avgRating: number;
  postCount: number;
  frequency: 'daily' | 'weekly' | 'monthly' | 'irregular';
  lastPost: Date;
  tags: string[];
  color: string;
  reason?: string;
  isRecommended: boolean;
  isTrending: boolean;
  isNew: boolean;
  isSubscribed: boolean;
  engagementScore: number;
  qualityScore: number;
  readingTime: number;
  language: string;
  price: 'free' | 'premium' | 'freemium';
  thumbnail?: string;
  website?: string;
  socialLinks?: {
    twitter?: string;
    linkedin?: string;
    website?: string;
  };
}

export interface QueueItem {
  id: string;
  userId: string;
  title: string;
  author: string;
  authorId: string;
  newsletter: string;
  newsletterId: string;
  readTime: number;
  priority: 'urgent' | 'normal' | 'low';
  addedAt: Date;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  tags: string[];
  excerpt: string;
  category: string;
  imageUrl?: string;
  contentUrl?: string;
  readingProgress?: number;
  lastReadAt?: Date;
  notes?: string;
}

export interface BreakingNewsArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  author: string;
  publishedAt: Date;
  updatedAt: Date;
  category: 'breaking' | 'urgent' | 'important' | 'regular';
  priority: 'critical' | 'high' | 'medium' | 'low';
  source: string;
  tags: string[];
  isRead: boolean;
  isBookmarked: boolean;
  wordCount: number;
  readTime: number;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  relatedArticles?: string[];
  socialShares?: {
    twitter: number;
    linkedin: number;
    facebook: number;
  };
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  count: number;
  description?: string;
}

export interface ReadingStats {
  totalArticles: number;
  readArticles: number;
  readingTime: number;
  averageReadingTime: number;
  readingStreak: number;
  favoriteCategories: string[];
  readingGoals: {
    daily: number;
    weekly: number;
    monthly: number;
  };
  achievements: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    unlockedAt: Date;
  }>;
}

class ReaderService {
  // Get recommended newsletters
  async getRecommendedNewsletters(userId: string, limitCount: number = 20): Promise<Newsletter[]> {
    try {
      const result = await getRecommendedNewsletters({
        userId,
        limit: limitCount
      });
      
      return (result.data as any).newsletters.map((newsletter: any) => ({
        ...newsletter,
        lastPost: new Date(newsletter.lastPost)
      }));
    } catch (error) {
      console.error('Error fetching recommended newsletters:', error);
      throw error;
    }
  }

  // Get reading queue
  async getReadingQueue(userId: string): Promise<QueueItem[]> {
    try {
      const result = await getReadingQueue({ userId });
      
      return (result.data as any).queueItems.map((item: any) => ({
        ...item,
        addedAt: new Date(item.addedAt),
        lastReadAt: item.lastReadAt ? new Date(item.lastReadAt) : undefined
      }));
    } catch (error) {
      console.error('Error fetching reading queue:', error);
      throw error;
    }
  }

  // Add article to reading queue
  async addToQueue(userId: string, article: Omit<QueueItem, 'id' | 'userId' | 'addedAt'>): Promise<string> {
    try {
      const result = await addToReadingQueue({
        userId,
        article: {
          ...article,
          addedAt: new Date()
        }
      });
      
      return (result.data as any).queueItemId;
    } catch (error) {
      console.error('Error adding to reading queue:', error);
      throw error;
    }
  }

  // Remove article from reading queue
  async removeFromQueue(queueItemId: string): Promise<void> {
    try {
      await removeFromReadingQueue({ queueItemId });
    } catch (error) {
      console.error('Error removing from reading queue:', error);
      throw error;
    }
  }

  // Update reading progress
  async updateProgress(queueItemId: string, progress: number, notes?: string): Promise<void> {
    try {
      await updateReadingProgress({
        queueItemId,
        progress,
        notes,
        lastReadAt: new Date()
      });
    } catch (error) {
      console.error('Error updating reading progress:', error);
      throw error;
    }
  }

  // Get breaking news
  async getBreakingNews(limitCount: number = 10): Promise<BreakingNewsArticle[]> {
    try {
      const result = await getBreakingNews({ limit: limitCount });
      
      return (result.data as any).articles.map((article: any) => ({
        ...article,
        publishedAt: new Date(article.publishedAt),
        updatedAt: new Date(article.updatedAt)
      }));
    } catch (error) {
      console.error('Error fetching breaking news:', error);
      throw error;
    }
  }

  // Get categories
  async getCategories(): Promise<Category[]> {
    try {
      const categoriesRef = collection(db, 'newsletterCategories');
      const q = query(categoriesRef, orderBy('count', 'desc'));
      
      const snapshot = await getDocs(q);
      const categories: Category[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        categories.push({
          id: doc.id,
          ...data
        } as Category);
      });
      
      return categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Return default categories if error
      return [
        { id: 'technology', name: 'Technology', icon: 'Zap', color: 'blue', count: 245 },
        { id: 'design', name: 'Design', icon: 'Palette', color: 'purple', count: 189 },
        { id: 'business', name: 'Business', icon: 'TrendingUp', color: 'green', count: 156 },
        { id: 'science', name: 'Science', icon: 'Microscope', color: 'orange', count: 134 },
        { id: 'news', name: 'News', icon: 'Globe', color: 'red', count: 167 }
      ];
    }
  }

  // Search newsletters
  async searchNewsletters(searchQuery: string, category?: string, limitCount: number = 20): Promise<Newsletter[]> {
    try {
      const newslettersRef = collection(db, 'newsletters');
      let q = query(newslettersRef, where('isPublic', '==', true));
      
      if (category) {
        q = query(newslettersRef, where('isPublic', '==', true), where('category', '==', category));
      }
      
      const snapshot = await getDocs(q);
      const newsletters: Newsletter[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        const newsletter = {
          id: doc.id,
          ...data,
          lastPost: data.lastPost?.toDate() || new Date()
        } as Newsletter;
        
        // Simple text search
        const searchText = `${newsletter.title} ${newsletter.description} ${newsletter.tags.join(' ')} ${newsletter.author}`.toLowerCase();
        if (searchText.includes(searchQuery.toLowerCase())) {
          newsletters.push(newsletter);
        }
      });
      
      return newsletters
        .sort((a, b) => b.engagementScore - a.engagementScore)
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error searching newsletters:', error);
      throw error;
    }
  }

  // Get reading stats
  async getReadingStats(userId: string): Promise<ReadingStats> {
    try {
      const queueItems = await this.getReadingQueue(userId);
      
      const totalArticles = queueItems.length;
      const readArticles = queueItems.filter(item => item.isRead).length;
      const readingTime = queueItems.reduce((sum, item) => sum + item.readTime, 0);
      const averageReadingTime = totalArticles > 0 ? readingTime / totalArticles : 0;
      
      // Calculate reading streak (simplified)
      const readingStreak = this.calculateReadingStreak(queueItems);
      
      // Get favorite categories
      const categoryCounts = queueItems.reduce((acc, item) => {
        acc[item.category] = (acc[item.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const favoriteCategories = Object.entries(categoryCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([category]) => category);
      
      // Get achievements (simplified)
      const achievements = this.calculateAchievements(queueItems, readingStreak);
      
      return {
        totalArticles,
        readArticles,
        readingTime,
        averageReadingTime: Math.round(averageReadingTime * 100) / 100,
        readingStreak,
        favoriteCategories,
        readingGoals: {
          daily: 5,
          weekly: 25,
          monthly: 100
        },
        achievements
      };
    } catch (error) {
      console.error('Error fetching reading stats:', error);
      throw error;
    }
  }

  // Calculate reading streak
  private calculateReadingStreak(queueItems: QueueItem[]): number {
    const readItems = queueItems
      .filter(item => item.isRead && item.lastReadAt)
      .sort((a, b) => b.lastReadAt!.getTime() - a.lastReadAt!.getTime());
    
    if (readItems.length === 0) return 0;
    
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < readItems.length; i++) {
      const readDate = new Date(readItems[i].lastReadAt!);
      readDate.setHours(0, 0, 0, 0);
      
      const daysDiff = Math.floor((today.getTime() - readDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === streak) {
        streak++;
      } else if (daysDiff === streak + 1) {
        streak++;
        today.setDate(today.getDate() - 1);
      } else {
        break;
      }
    }
    
    return streak;
  }

  // Calculate achievements
  private calculateAchievements(queueItems: QueueItem[], readingStreak: number): Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    unlockedAt: Date;
  }> {
    const achievements = [];
    const readCount = queueItems.filter(item => item.isRead).length;
    
    if (readCount >= 10) {
      achievements.push({
        id: 'first_10',
        name: 'First 10',
        description: 'Read your first 10 articles',
        icon: 'BookOpen',
        unlockedAt: new Date()
      });
    }
    
    if (readCount >= 50) {
      achievements.push({
        id: 'dedicated_reader',
        name: 'Dedicated Reader',
        description: 'Read 50 articles',
        icon: 'Award',
        unlockedAt: new Date()
      });
    }
    
    if (readingStreak >= 7) {
      achievements.push({
        id: 'week_streak',
        name: 'Week Warrior',
        description: '7-day reading streak',
        icon: 'Flame',
        unlockedAt: new Date()
      });
    }
    
    if (readingStreak >= 30) {
      achievements.push({
        id: 'month_streak',
        name: 'Month Master',
        description: '30-day reading streak',
        icon: 'Crown',
        unlockedAt: new Date()
      });
    }
    
    return achievements;
  }

  // Subscribe to newsletter
  async subscribeToNewsletter(userId: string, newsletterId: string): Promise<void> {
    try {
      const subscriptionRef = doc(db, 'newsletterSubscriptions', `${userId}_${newsletterId}`);
      await setDoc(subscriptionRef, {
        userId,
        newsletterId,
        subscribedAt: new Date(),
        status: 'active'
      });
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      throw error;
    }
  }

  // Unsubscribe from newsletter
  async unsubscribeFromNewsletter(userId: string, newsletterId: string): Promise<void> {
    try {
      const subscriptionRef = doc(db, 'newsletterSubscriptions', `${userId}_${newsletterId}`);
      await updateDoc(subscriptionRef, {
        status: 'unsubscribed',
        unsubscribedAt: new Date()
      });
    } catch (error) {
      console.error('Error unsubscribing from newsletter:', error);
      throw error;
    }
  }

  // Get user's subscriptions
  async getUserSubscriptions(userId: string): Promise<string[]> {
    try {
      const subscriptionsRef = collection(db, 'newsletterSubscriptions');
      const q = query(
        subscriptionsRef,
        where('userId', '==', userId),
        where('status', '==', 'active')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data().newsletterId);
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const readerService = new ReaderService();
export default readerService;
